'use client';
import { AddShoppingCart, Input, RotateRight } from '@mui/icons-material';
import { AppBar, Box, Button, Toolbar } from '@mui/material';
import CourseBottomBar from '@/components/course/course-bottom-bar';
import ButtonStartNewTestRun from '@/components/test/button-start-new-test-run';
import { useFlashcardDecksAndStats } from '@/hooks';
import { useCourses } from '@/providers';
import { getFirstNavigableChapter } from '@/utils/client/course-curriculum';
import { getAddProductToCartUrl, useRoute } from '@/utils/client/route';

export default function BarBottom() {
  const { isRoute, goToRoute } = useRoute();
  const { selectedCourse, courseStatus, canRenewTest } = useCourses();
  const isFlashcardsTab = isRoute('myCourseFlashcards');
  const { data: decksAndStats } = useFlashcardDecksAndStats(
    isFlashcardsTab ? selectedCourse?.id : undefined,
  );

  if (!selectedCourse) return null;

  const { curriculum, type } = selectedCourse;
  const hasMembership = courseStatus?.hasMembership;
  const isTest = type === 'Test';
  const isSubmittedTest = isTest && courseStatus?.status === 'submitted';
  const firstExerciseOrUnitInfo = getFirstNavigableChapter(curriculum);

  const onStartLearning = () => {
    if (isFlashcardsTab) {
      const firstDeckSlug = decksAndStats?.decks?.[0]?.deck.slug;
      if (firstDeckSlug) {
        goToRoute('myCourseFlashcards', {
          courseSlug: selectedCourse.slug,
          deckSlug: firstDeckSlug,
        });
        return;
      }
    }
    const firstChapter = firstExerciseOrUnitInfo;
    if (firstChapter?.item.slug) {
      goToRoute('myCourseLearn', {
        courseSlug: selectedCourse.slug,
        chapterSlug: firstChapter.item.slug,
      });
    }
  };

  const onContinueLearning = () => {
    if (courseStatus?.lastChapter) {
      goToRoute('myCourseLearn', {
        courseSlug: selectedCourse.slug,
        chapterSlug: courseStatus.lastChapter.slug,
      });
    }
  };

  const getContinueBtnLabel = (): string => {
    const typeName = courseStatus?.lastChapter?.type === 'Exercise' ? 'Übung' : 'Lektion';
    return `${typeName} fortsetzen`;
  };

  const renderTabsActionButton = () => {
    if (canRenewTest) {
      return <ButtonStartNewTestRun size="small" />;
    }

    if (isSubmittedTest) {
      return null;
    }

    if (!courseStatus?.lastChapter) {
      return (
        <Button
          size="small"
          variant="contained"
          color={hasMembership ? 'primary' : 'secondary'}
          startIcon={<Input />}
          onClick={onStartLearning}>
          Beginnen
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant="contained"
        color={hasMembership ? 'primary' : 'secondary'}
        startIcon={<RotateRight />}
        onClick={onContinueLearning}>
        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
          {getContinueBtnLabel()}
        </Box>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          Fortsetzen
        </Box>
      </Button>
    );
  };

  return (
    <AppBar
      position="fixed"
      color="default"
      component="footer"
      sx={{
        top: 'auto',
        bottom: 0,
        '& > .MuiToolbar-root': {
          minHeight: 50,
        },
      }}>
      <Toolbar
        sx={{
          alignItems: 'center',
          width: '100%',
          margin: '0 auto',
          px: 2,
          maxWidth: (theme) => theme.breakpoints.values.lg,
        }}>
        {isRoute('myCourseLearn') && <CourseBottomBar />}
        {isRoute('myCourseTabs') && (
          <>
            {((selectedCourse && !hasMembership) || isTest) && (
              <Button
                size="small"
                variant="contained"
                color={hasMembership ? 'secondary' : 'primary'}
                startIcon={<AddShoppingCart />}
                target="_blank"
                href={getAddProductToCartUrl(selectedCourse.wpProductId)}>
                Warenkorb
              </Button>
            )}

            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }} />

            {renderTabsActionButton()}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}
